import React, { useState, useEffect } from 'react';
import CRUDList from '../Common/CRUDList';
import DateInput from '../Common/DateInput';
import { tickets, bookings, passengers } from '../../services/api';
import { formatDate } from '../../utils/dateFormat';
import { ticketStatuses, ticketStatusColors, changeByTypes } from '../../constants/travelEnums';

function TicketForm({ formData, setFormData }) {
  const [bookingsList, setBookingsList] = useState([]);
  const [passengersList, setPassengersList] = useState([]);
  const [ticketsList, setTicketsList] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingReplacementId, setPendingReplacementId] = useState(null);
  const isReplacement = formData.ticket_init_id != null;

  useEffect(() => {
    loadBookings();
    loadPassengers();
    loadTickets();
  }, []);

  const loadBookings = async () => {
    try {
      const response = await bookings.getAll();
      setBookingsList(response.data);
    } catch (err) {
      console.error('Failed to load bookings', err);
    }
  };

  const loadPassengers = async () => {
    try {
      const response = await passengers.getAll();
      setPassengersList(response.data);
    } catch (err) {
      console.error('Failed to load passengers', err);
    }
  };

  const loadTickets = async () => {
    try {
      const response = await tickets.getAll();
      setTicketsList(response.data);
    } catch (err) {
      console.error('Failed to load tickets', err);
    }
  };

  const handleReplacementChange = (e) => {
    const replacedId = e.target.value ? parseInt(e.target.value) : null;
    if (replacedId) {
      setPendingReplacementId(replacedId);
      setShowConfirmModal(true);
    } else {
      setFormData({ ...formData, replaced_ticket_id: null, ticket_init_id: null, ticket_chg_num: 0, prev_change_by: null });
    }
  };

  const confirmReplacement = () => {
    const replacedId = pendingReplacementId;
    const replacedTicket = ticketsList.find(t => t.ticket_id === replacedId);
    const initId = replacedTicket?.ticket_init_id || replacedId;
    const chainTickets = ticketsList.filter(t =>
      t.ticket_init_id === initId || t.ticket_id === initId
    );
    const maxChgNum = Math.max(...chainTickets.map(t => t.ticket_chg_num || 0));
    setFormData({
      ...formData,
      replaced_ticket_id: replacedId,
      ticket_init_id: initId,
      ticket_chg_num: maxChgNum + 1,
      ticket_booking_id: replacedTicket?.ticket_booking_id || formData.ticket_booking_id,
      ticket_passenger_id: replacedTicket?.ticket_passenger_id || formData.ticket_passenger_id,
      prev_change_by: 10
    });
    setShowConfirmModal(false);
    setPendingReplacementId(null);
  };

  const cancelReplacement = () => {
    setShowConfirmModal(false);
    setPendingReplacementId(null);
  };

  const getSelectedTicketLabel = () => {
    if (pendingReplacementId) {
      const t = ticketsList.find(t => t.ticket_id === pendingReplacementId);
      if (t) return `${t.ticket_number || `Ticket ${t.ticket_id}`} - ${t.booking_pnr} (${t.passenger_name})`;
    }
    return '';
  };

  return (
    <>
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Replacement</h3>
            <p>Are you sure you want to replace ticket <strong>{getSelectedTicketLabel()}</strong>? This action will mark the original ticket as "Replaced".</p>
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={cancelReplacement}>No</button>
              <button type="button" className="btn btn-danger" onClick={confirmReplacement}>Yes</button>
            </div>
          </div>
        </div>
      )}

      {!formData.ticket_id && (
        <div className="form-group replacement-warning">
          <label>Original Ticket (for replacement)</label>
          <select
            value={formData.replaced_ticket_id || ''}
            onChange={handleReplacementChange}
          >
            <option value="">None (original ticket)</option>
            {ticketsList
              .filter(t => t.ticket_status === 10)
              .map(t => (
                <option key={t.ticket_id} value={t.ticket_id}>
                  {t.ticket_number || `Ticket ${t.ticket_id}`} - {t.booking_pnr} ({t.passenger_name})
                </option>
              ))}
          </select>
          {isReplacement && (
            <small className="text-muted">Change #: {formData.ticket_chg_num}</small>
          )}
        </div>
      )}

      {isReplacement && !formData.ticket_id && (
        <div className="form-group">
          <label>Changed By *</label>
          <select
            value={formData.prev_change_by || ''}
            onChange={(e) => setFormData({ ...formData, prev_change_by: parseInt(e.target.value) })}
            required
          >
            {Object.entries(changeByTypes).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
          </select>
        </div>
      )}

      <div className="form-group">
        <label>Booking *</label>
        <select
          value={formData.ticket_booking_id || ''}
          onChange={(e) => setFormData({ ...formData, ticket_booking_id: parseInt(e.target.value) })}
          required
        >
          <option value="">Select booking</option>
          {bookingsList.map(b => (
            <option key={b.booking_id} value={b.booking_id}>
              {b.booking_pnr} ({b.booking_date})
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Passenger *</label>
        <select
          value={formData.ticket_passenger_id || ''}
          onChange={(e) => setFormData({ ...formData, ticket_passenger_id: parseInt(e.target.value) })}
          required
        >
          <option value="">Select passenger</option>
          {passengersList.map(p => (
            <option key={p.passenger_id} value={p.passenger_id}>
              {p.passenger_name || `Passenger ${p.passenger_id}`}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>Ticket Date *</label>
        <DateInput
          value={formData.ticket_date || ''}
          onChange={(value) => setFormData({ ...formData, ticket_date: value })}
          required
        />
      </div>

      <div className="form-group">
        <label>Ticket Number</label>
        <input
          type="text"
          value={formData.ticket_number || ''}
          onChange={(e) => setFormData({ ...formData, ticket_number: e.target.value })}
        />
      </div>

      <div className="form-group">
        <label>Status *</label>
        <select
          value={formData.ticket_status || ''}
          onChange={(e) => setFormData({ ...formData, ticket_status: parseInt(e.target.value) })}
          required
        >
          <option value="">Select status</option>
          <option value="10">Active</option>
          <option value="20">Replaced</option>
          <option value="30">Cancelled</option>
        </select>
      </div>

      {(formData.ticket_status === 20 || formData.ticket_status === 30) && (
        <>
          <div className="form-group">
            <label>Change Date</label>
            <DateInput
              value={formData.ticket_change_date || ''}
              onChange={(value) => setFormData({ ...formData, ticket_change_date: value })}
            />
          </div>

          <div className="form-group">
            <label>Changed By</label>
            <select
              value={formData.ticket_change_by || ''}
              onChange={(e) => setFormData({ ...formData, ticket_change_by: e.target.value ? parseInt(e.target.value) : null })}
            >
              <option value="">Select who changed</option>
              {Object.entries(changeByTypes).map(([key, value]) => (
                <option key={key} value={key}>{value}</option>
              ))}
            </select>
          </div>
        </>
      )}
    </>
  );
}

function TicketsList() {
  return (
    <CRUDList
      title="Tickets"
      api={tickets}
      columns={['ID', 'Booking PNR', 'Passenger', 'Ticket Number', 'Date', 'Status']}
      FormComponent={TicketForm}
      defaultFormData={{
        replaced_ticket_id: null,
        ticket_init_id: null,
        ticket_chg_num: 0,
        ticket_booking_id: '',
        ticket_passenger_id: '',
        ticket_date: new Date().toISOString().split('T')[0],
        ticket_number: '',
        ticket_status: 10,
        ticket_change_date: null,
        ticket_change_by: null,
        prev_change_by: null
      }}
      useOldStyle={true}
      renderRow={(item) => (
        <>
          <td>{item.ticket_id}</td>
          <td><strong>{item.booking_pnr}</strong></td>
          <td>{item.passenger_name || `Passenger ${item.ticket_passenger_id}`}</td>
          <td>{item.ticket_number || '-'}</td>
          <td>{formatDate(item.ticket_date)}</td>
          <td>
            <span className={`badge ${ticketStatusColors[item.ticket_status]}`}>
              {ticketStatuses[item.ticket_status]}
            </span>
          </td>
        </>
      )}
    />
  );
}

export default TicketsList;
